import type { InjectionKey, Ref } from 'vue';
import type { OpenUrl } from '@/common/composables'

// Ключи для инъекций платформы
export const PlatformDialogLayoutKey: InjectionKey<
  import('vue').ComputedRef<{ compact: boolean; sized: boolean }> | { compact: boolean; sized: boolean }
> = Symbol('platform-dialog-layout')

/** Unique vertical scroll owner for the current form (see formScrollOwner.ts). */
export const FormScrollOwnerKey: InjectionKey<
  import('vue').ComputedRef<import('@/common/utils/formScrollOwner').FormScrollOwner>
> = Symbol('form-scroll-owner')

export const RegisterFormKey: InjectionKey<(formId: string) => void> = Symbol('register-form')
export const UnRegisterFormKey: InjectionKey<(formId: string) => void> = Symbol('unregister-form')
export const RegisterPanelKey: InjectionKey<(params: {
  formId: string
  panelId: string
  instanceKey?: string
  runtimeId?: string
  setVisible?: (visible: boolean) => void
}) => void> = Symbol('register-panel')
export const UnRegisterPanelKey: InjectionKey<(params: {
  formId: string
  panelId: string
  instanceKey?: string
  runtimeId?: string
}) => void> = Symbol('unregister-panel')
export const RegisterOverlayKey: InjectionKey<(params: {
  name: string
  overlayId: string
  formId: string
  setVisible: (visible: boolean) => void
  isVisible: () => boolean
  isDialog?: boolean
}) => void> = Symbol('register-overlay')
export const UnRegisterOverlayKey: InjectionKey<(params: { name: string; formId: string }) => void> = Symbol('unregister-overlay')
export const FormOverlayHostKey: InjectionKey<import('vue').Ref<HTMLElement | null>> = Symbol('form-overlay-host')
/** Platform layer for floating overlays (Swing JLayeredPane). */
export const FormOverlayLayerKey: InjectionKey<import('vue').Ref<HTMLElement | null>> =
  Symbol('form-overlay-layer')
export const FormOverlayRemeasureKey: InjectionKey<(expanded: boolean) => void> = Symbol('form-overlay-remeasure')
export const RegisterElementKey: InjectionKey<(params: any) => void> = Symbol('register-element')
export const UnRegisterElementKey: InjectionKey<(params: any) => void> = Symbol('unregister-element')
export const RegisterActionKey: InjectionKey<(ctx: any, hooks: any) => void> = Symbol('register-action')
export const UnRegisterActionKey: InjectionKey<(ctx: any) => void> = Symbol('unregister-action')
export const SetValueKey: InjectionKey<(params: any) => void> = Symbol('set-value')
export const OnActionKey: InjectionKey<(context: any) => void> = Symbol('on-action')
export const OnElementKey: InjectionKey<(ctx: any) => void> = Symbol('on-element')
export const GetFormKey: InjectionKey<(params: any) => any> = Symbol('get-form')
export const GetPageDataKey: InjectionKey<(params: any) => any> = Symbol('get-page-data')
export const RunActionByNameKey: InjectionKey<(name: string) => void> = Symbol('run-action-by-name')
export const RunActionKey: InjectionKey<(ctx: any) => void> = Symbol('run-action')
export const RunEventKey: InjectionKey<(cmd: string, ctx: any) => void> = Symbol('run-event')
export const RunCodeKey: InjectionKey<(cmd: string, selectedData?: any) => void> = Symbol('run-code')
export const ReloadElementKey: InjectionKey<(elementName: string, data: any, actionContext?: any) => Promise<any>> = Symbol('reload-element')
export const EnKey: InjectionKey<any> = Symbol('en')
export const OpenUrls: InjectionKey<(URL?:OpenUrl) => void> = Symbol('open-urls')
/** Пропустить следующий beforeunload. */
export const SkipNextPageLeaveConfirmKey: InjectionKey<() => void> = Symbol('skip-next-page-leave-confirm')

// Ключ для FormProvider
export const FormProviderKey: InjectionKey<{
  getForm: (path: any) => any
  getPageData?: (params: {
    formId?: string | number
    pageId?: string | number
    flatten?: boolean
  }) => any
  collectLiveFieldOverrides?: (
    formId?: string | number,
    options?: { pageDataSlot?: string },
  ) => Record<string, { type: string; value: unknown }>
  setValue: (data: any) => void
  registerElement: (data: any) => void
  unRegisterElement: (data: any) => void
  isPageLazy: () => boolean
  runActionByName: (name: string) => void
  setActionData?: (data?: any) => void
  runAction: (data: any) => void
  runEvent: (code: string, context?: any) => void
  runCode: (
    code: string,
    selectedData?: any,
    context?: {
      formId?: string | number
      pageDataSlot?: string
      panelId?: string | number
      tilesRowScope?: import('@/common/utils/tilesRowPanelData').SwingTilesRowScopeLike | null
      preloadPageFields?: boolean
    },
  ) => void | Promise<unknown>
  getTilesRowScope?: () => import('@/common/utils/tilesRowPanelData').SwingTilesRowScopeLike | null
  getPersistedScopedVisibility?: (registryName: string) => boolean | undefined
  getTilesRowActionVisibilityStore?: () => import('vue').Ref<
    import('@/common/utils/tilesSetInlineVisibility').TilesRowActionVisibilityMap
  >
  /** Bumped after reload/panel replaces tiles tables — MTiles re-runs inlineAfterLoad. */
  getTilesInlineAfterLoadEpoch?: () => import('vue').Ref<number>
  /** Table field names from the last reload/panel that bumped the epoch (scoped re-run). */
  getTilesInlineAfterLoadReloadTables?: () => import('vue').Ref<readonly string[]>
  onElement: (context: any) => void
  reloadElement?: (elementName: string, data?: any, actionContext?: any) => Promise<any>
  reloadPanel?: (panelName: string | number, data?: any) => Promise<any>
  reloadForm?: (data?: any, context?: Record<string, unknown>) => Promise<any>
  /** Window.doStackAction(-1): POST /platform/action STACK, not /reload/form. */
  reloadFormStack?: (context?: Record<string, unknown>) => Promise<any>
  resolveElementTableSwingMethod?: (
    entity: Record<string, unknown>,
    method: 'getSelectedPrimaryKey' | 'getPrimaryKeyColumns' | 'getType',
  ) => unknown
  refillElement: (elementName: string, fieldData: any, formId?: string) => void
  queuePendingOverlayRefill?: (payload: {
    fieldName: string
    fieldData: unknown
    formId?: string
  }) => void
  flushPendingOverlayRefills?: (shownOverlayPageIds?: Iterable<string>) => void
  getOverlayDuplicatedFieldNames?: (formId?: string) => Iterable<string>
  rememberShownOverlayPageIdsFromVisibilities?: (raw: unknown) => void
  setVisible?: (
    name: string,
    isVisible: boolean,
    scope?: { formId?: string; panelId?: string },
  ) => void
  /** persist по умолчанию true; processStates/replay — `{ persist: false }`. */
  setEditable?: (name: string, isEditable: boolean, options?: { persist?: boolean }) => void
  setEditableByLevel?: (
    level: string,
    id: string | number,
    isEditable: boolean,
    options?: { persist?: boolean },
  ) => void
  setEnable?: (name: string, isEnabled: boolean) => void
  requestFocus?: (name: string) => void
  scrollToElement: (name: string) => void
  empty?: (name: string) => void
  registerTableSelection?: (formId: string | number, panelId: string | number | undefined, elementId: string | number | undefined, fieldName: string, selectedRows: any[]) => void
  getTableSelection?: (formId: string | number | undefined, panelId: string | number | undefined, elementId: string | number | undefined) => { fieldName: string; rows: any[] } | null
  setElementError?: (name: string, error: string | null) => void
  /** Form-level error banner (en.setFormError). */
  setFormError?: (message: string | null, slotId?: string | number) => void
  removeTextFieldBorder?: (name: string) => void
  setCursorPositionToEnd?: (name: string) => void
  expandAllElement?: (name: string) => void
  openSuggesterElement?: (name: string, formId?: string) => void
  reloadTreeTableNode?: (
    name: string,
    rowNum: number,
    needReloadParent?: boolean,
    selectedNodeData?: unknown,
  ) => Promise<any>
  setMultipleLimitElement?: (name: string, limit: number) => void
  setAutocompleteElement?: (name: string, mode: string) => void
  setEnableSuggestEditElement?: (name: string, enabled: boolean) => void
  setHasEditorElement?: (name: string, hasEditor: boolean) => void
  setMultipleDelimiterElement?: (name: string, delimiter: string) => void
  appendBytesElement?: (name: string, fileName: string, base64Data: string) => void
  appendFileCopiesElement?: (name: string, attachments: any) => void
  setVerifyRegexp?: (name: string, regexp: string) => void
  getElements: () => any
  getElementsByKey?: () => any
  /** Подгрузка данных ленивой вкладки (page lazy="true") */
  reloadLazyPage?: (pageId: number, formId?: string) => Promise<void>
  setVisibleByLevel?: (
    level: string,
    id: string | number,
    visible: boolean,
    options?: { propagateChildren?: boolean },
  ) => void
  /** Общая видимость по XML element id (все mount Element.vue для Label). */
  elementShellVisibleByElementId?: Ref<Record<string, boolean>>
  setElementShellVisibleById?: (elementId: string | number, visible: boolean) => void
  getOverlays?: () => Record<string, { name: string; overlayId: string; isDialog?: boolean }>
  /** После submit-поиска TreeTable — применить результат из pageFields */
  applyPendingTreeTableSearch?: (formId: string | number) => Promise<void>
  /** Вернуть поля из pagesData в element registry (смена вкладки KeepAlive). */
  resyncTabSlotFields?: (
    pageSlotId: string,
    options?: { skipSnapshotRestore?: boolean },
  ) => void
  /** Re-apply is_visible states from page store after dialog close or tab remount. */
  replayPageVisibilityStatesForSlot?: (pageSlotId: string) => void
  /** Commit pending EDITABLE cell editors before action payload snapshot. */
  flushPendingEditableTableEdits?: (formId?: string | number) => Promise<boolean>
  /** Re-run element onChange for EDITABLE rows with partially filled columns (derived fields). */
  retryEditableTableOnChangeIfNeeded?: (formId?: string | number) => Promise<void>
  /** Await in-flight element onChange/runAction started by editable commit. */
  awaitPendingElementEvent?: () => Promise<void>
  syncPageDataToRegistry?: (
    formId?: string | number,
    pageDataSlot?: string,
    options?: Record<string, unknown>,
  ) => void
}> = Symbol('form-provider')

// Ключ для ElementContext
export const ElementContextKey: InjectionKey<{
  formId: string
  panelId?: string
  elementId?: number
  isReadOnly?: boolean
  jsName?: string
  pageId?: string | number
  pageDataSlot?: string
}> = Symbol('element-context')

/** Одна строка таблицы Tiles */
export const SwingTilesRowScopeKey: InjectionKey<{
  tableFieldName: string
  rowIndex: number
  tilesElementId: number
  parentPanelId: string | number
  commitRowField: (fieldName: string, value: unknown) => void
  getRowFieldValue: (fieldName: string) => unknown
  getRowData?: () => unknown
  getActionVisibility?: (actionName: string) => boolean | undefined
  /** Reactive slice rowActionVisibility[rowIndex] для MAction. */
  rowActionVisibility?: import('vue').ComputedRef<Record<string, boolean>>
}> = Symbol('swing-tiles-row-scope')

/** Вложенная панель рендерится внутри строки Tiles */
export const SwingTilesEmbedKey: InjectionKey<boolean> = Symbol('swing-tiles-embed')

/** Row-local map видимости action из MTiles.setInlineVisibility (reactive ref). */
export const SwingTilesActionVisibilityMapKey: InjectionKey<
  import('vue').Ref<import('@/common/utils/tilesSetInlineVisibility').TilesRowActionVisibilityMap>
> = Symbol('swing-tiles-action-visibility-map')

/** true, если у Tiles есть непустой inlineAfterLoad (setInlineVisibility per-row). */
export const SwingTilesHasInlineAfterLoadKey: InjectionKey<
  import('vue').ComputedRef<boolean>
> = Symbol('swing-tiles-has-inline-after-load')

/** Имена element/action из setInlineVisibility в inlineAfterLoad (парсинг скрипта). */
export const SwingTilesInlineVisibilityTargetsKey: InjectionKey<
  import('vue').ComputedRef<ReadonlySet<string>>
> = Symbol('swing-tiles-inline-visibility-targets')
